import React, { useContext } from "react";
import {
  FormControl,
  FormLabel,
  Input,
  Textarea,
  FormErrorMessage,
  Checkbox,
} from "@chakra-ui/react";
import { camelCase } from "lodash";
import { formContext, ModelFieldValues } from "../formContext";

interface FormFieldProps {
  name: string;
  required?: boolean;
  label?: string;
  type?: string;
  onBlur?: () => void;
  maxLength?: number;
}

export const FormField = (props: FormFieldProps): JSX.Element => {
  const { label, name, type, required, onBlur, maxLength } = props;
  const fieldNameCamelCase = camelCase(name) as keyof ModelFieldValues;
  const { register, errors } = useContext(formContext);
  const isRequired = required ?? `${name} is required`;

  if (!register) {
    throw new Error(
      "FormField must be in formContext tree 'register' is not defined."
    );
  }

  if (!errors) {
    throw new Error(
      "FormField must be in formContext tree 'errors' is not defined"
    );
  }

  const inputProps = {
    type: type || "text",
    placeholder: type === "select" ? undefined : name,
    "data-cy": fieldNameCamelCase,
    ...register(fieldNameCamelCase, { required: isRequired }),
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const numberExceptions = ["e", "E", "+", "-", "."];

    if (type === "number" && numberExceptions.includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <FormControl
      isInvalid={!!errors[fieldNameCamelCase]}
      isRequired={!!isRequired}
      mb="4"
    >
      <FormLabel>{label || name}</FormLabel>
      {type === "textarea" ? (
        <Textarea {...inputProps} display="inline" maxLength={maxLength} />
      ) : type === "checkbox" ? (
        <Checkbox {...inputProps}>yes</Checkbox>
      ) : (
        <Input {...inputProps} onKeyDown={handleOnKeyDown} onBlur={onBlur} />
      )}
      <FormErrorMessage>{errors[fieldNameCamelCase]?.message}</FormErrorMessage>
    </FormControl>
  );
};
